import React from "react";
import {
  ArrowRight,
  Recycle,
  Factory,
  Building2,
  ShieldCheck,
  Activity,
  Waves,
  Droplets,
  CheckCircle2,
} from "lucide-react";
import PageHero1 from "../components/PageHero1";
import SEO from "../components/SEO";
import { assets } from "../data/siteData";

const solutionAreas = [
  "Decentralized wastewater treatment systems",
  "Treated water reuse support",
  "Site-specific process configuration",
  "Integration with monitoring and control systems",
  "O&M and lifecycle support",
  "Sustainable water management approach",
];

const applicationAreas = [
  {
    title: "Residential Complexes",
    text: "Compact and dependable treatment systems for apartment blocks, housing communities, and mixed-use developments.",
    icon: Building2,
  },
  {
    title: "Institutions & Campuses",
    text: "Wastewater treatment and reuse infrastructure for schools, colleges, hospitals, and public facilities.",
    icon: ShieldCheck,
  },
  {
    title: "Commercial & Hospitality",
    text: "Smart water stewardship systems for hotels, offices, commercial campuses, and guest-focused environments.",
    icon: Recycle,
  },
  {
    title: "Industrial Sites",
    text: "Engineered process-ready systems for industrial discharge control, reuse potential, and operational resilience.",
    icon: Factory,
  },
];

const valuePoints = [
  {
    title: "Reuse-Driven Engineering",
    text: "Built around treatment efficiency, site practicality, and long-term water recovery opportunities.",
    icon: Waves,
  },
  {
    title: "Monitoring Ready",
    text: "Designed for visibility, controls, and operational tracking across decentralized installations.",
    icon: Activity,
  },
  {
    title: "Reliable Lifecycle Support",
    text: "From deployment to O&M, Hydrotec focuses on performance stability and long-term site usability.",
    icon: CheckCircle2,
  },
];

export default function WastewaterPage() {
  return (
    <>
      <SEO
        title="Wastewater Treatment Plant in Kolkata, Newtown, West Bengal | STP Systems"
        description="Hydrotec Solutions designs and installs wastewater treatment plants (STP) for buildings, institutions, and industries in Kolkata, Newtown, Rajarhat, and West Bengal. Treated water reuse and sewage management solutions."
        keywords="wastewater treatment plant Kolkata, STP system Newtown, sewage treatment Rajarhat, wastewater management West Bengal, effluent treatment Kolkata, treated water reuse West Bengal"
        canonical="https://hydroteclimited.com/solutions/wastewater"
      />
      <PageHero1
        title="Wastewater Solutions"
        text="Wastewater treatment and reuse systems..."
        video={assets.video1}
      />

      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(14,165,233,0.16),transparent_28%),radial-gradient(circle_at_bottom_right,rgba(6,182,212,0.12),transparent_30%),linear-gradient(to_bottom,rgba(2,6,23,0.86),rgba(2,6,23,0.97))]" />
          <div className="absolute left-0 top-0 h-72 w-72 rounded-full bg-cyan-500/10 blur-3xl" />
          <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-sky-500/10 blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
          <div className="grid gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:gap-14">
            <div>
              <span className="inline-flex items-center rounded-full border border-sky-400/20 bg-sky-400/10 px-4 py-1.5 text-sm font-medium text-sky-300">
                Sustainable treatment. Smarter reuse.
              </span>

              <h2 className="mt-6 max-w-4xl text-4xl font-semibold leading-tight tracking-tight text-white sm:text-5xl lg:text-6xl">
                Practical wastewater treatment with{" "}
                <span className="bg-gradient-to-r from-sky-300 via-cyan-300 to-blue-400 bg-clip-text text-transparent">
                  reuse-focused thinking
                </span>
              </h2>

              <p className="mt-6 max-w-2xl text-base leading-8 text-slate-300 sm:text-lg">
                Hydrotec wastewater solutions are designed to support
                treatment, discharge management, reuse potential, and
                operational reliability across decentralized and site-specific
                installations.
              </p>

              <p className="mt-5 max-w-2xl text-base leading-8 text-slate-400 sm:text-lg">
                These systems can be adapted for residential complexes,
                institutions, public sites, hospitality, commercial campuses,
                and industrial applications requiring stronger water
                stewardship and long-term operating confidence.
              </p>

              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  href="/contact"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-slate-950 transition duration-300 hover:-translate-y-0.5 hover:bg-sky-50"
                >
                  Talk to Us
                  <ArrowRight className="h-4 w-4" />
                </a>

                <a
                  href="/solutions"
                  className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-semibold text-white transition duration-300 hover:border-sky-300/30 hover:bg-white/10"
                >
                  Explore More Solutions
                </a>
              </div>

              <div className="mt-12 grid gap-4 sm:grid-cols-3">
                <div className="rounded-3xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-sky-400/15 text-sky-300">
                    <Recycle className="h-5 w-5" />
                  </div>
                  <p className="mt-4 text-lg font-semibold text-white">
                    Reuse Potential
                  </p>
                  <p className="mt-2 text-sm leading-6 text-slate-400">
                    Built to support sustainable water recovery across varied site conditions.
                  </p>
                </div>

                <div className="rounded-3xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-cyan-400/15 text-cyan-300">
                    <Activity className="h-5 w-5" />
                  </div>
                  <p className="mt-4 text-lg font-semibold text-white">
                    Monitoring Ready
                  </p>
                  <p className="mt-2 text-sm leading-6 text-slate-400">
                    Integrates with monitoring and control systems for better visibility.
                  </p>
                </div>

                <div className="rounded-3xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-400/15 text-blue-300">
                    <Droplets className="h-5 w-5" />
                  </div>
                  <p className="mt-4 text-lg font-semibold text-white">
                    Site Specific
                  </p>
                  <p className="mt-2 text-sm leading-6 text-slate-400">
                    Configured for practical deployment across decentralized installations.
                  </p>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-sky-500/10 via-cyan-400/10 to-blue-500/10 blur-2xl" />
              <div className="relative rounded-[2rem] border border-white/10 bg-white/6 p-7 shadow-[0_25px_90px_rgba(0,0,0,0.35)] backdrop-blur-xl">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-400/15 text-sky-300">
                    <Recycle className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">
                      Solution Areas
                    </h3>
                    <p className="text-sm text-slate-400">
                      Engineered for performance, adaptability, and lifecycle value
                    </p>
                  </div>
                </div>

                <div className="mt-7 space-y-3">
                  {solutionAreas.map((item) => (
                    <div
                      key={item}
                      className="flex items-start gap-3 rounded-2xl border border-white/8 bg-slate-900/55 px-4 py-4 transition duration-300 hover:border-sky-400/20 hover:bg-slate-900/70"
                    >
                      <span className="mt-1.5 h-2.5 w-2.5 rounded-full bg-gradient-to-r from-sky-400 to-cyan-300" />
                      <span className="text-sm leading-7 text-slate-200 sm:text-base">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="mt-8 rounded-3xl border border-cyan-400/15 bg-gradient-to-r from-sky-500/10 via-cyan-400/10 to-blue-500/10 p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-sky-300">
                    Hydrotec Approach
                  </p>
                  <p className="mt-3 text-sm leading-7 text-slate-300">
                    Designed to balance treatment quality, operational
                    practicality, and long-term water stewardship across
                    buildings, institutions, communities, and industrial sites.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-900 py-20 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm font-medium text-cyan-300">
              Applications across sectors
            </span>
            <h2 className="mt-6 text-3xl font-semibold tracking-tight sm:text-4xl lg:text-5xl">
              Built for multiple wastewater use cases
            </h2>
            <p className="mt-5 text-base leading-8 text-slate-400 sm:text-lg">
              Hydrotec configures wastewater systems for a wide range of site
              conditions, treatment requirements, and reuse goals.
            </p>
          </div>

          <div className="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            {applicationAreas.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="group rounded-[2rem] border border-white/10 bg-gradient-to-b from-white/8 to-white/[0.03] p-7 transition duration-300 hover:-translate-y-1 hover:border-sky-400/25 hover:shadow-[0_20px_60px_rgba(14,165,233,0.08)]"
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-sky-400/10 text-sky-300 transition duration-300 group-hover:bg-sky-400/15">
                    <Icon className="h-7 w-7" />
                  </div>

                  <h3 className="mt-6 text-xl font-semibold text-white">
                    {item.title}
                  </h3>

                  <p className="mt-4 text-sm leading-7 text-slate-400">
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-slate-950 py-20 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(14,165,233,0.08),transparent_30%)]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-3">
            {valuePoints.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className="rounded-[2rem] border border-white/10 bg-white/5 p-8 backdrop-blur-sm transition duration-300 hover:-translate-y-1 hover:border-cyan-300/20"
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-400/20 to-cyan-400/10 text-sky-300">
                    <Icon className="h-7 w-7" />
                  </div>

                  <h3 className="mt-6 text-2xl font-semibold text-white">
                    {item.title}
                  </h3>

                  <p className="mt-4 text-base leading-8 text-slate-400">
                    {item.text}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-gradient-to-r from-sky-700 via-cyan-700 to-slate-950 py-20 text-white">
        <div className="absolute inset-0 bg-black/15" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-[2rem] border border-white/10 bg-white/8 p-8 backdrop-blur-md sm:p-10 lg:flex lg:items-center lg:justify-between">
            <div className="max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-[0.24em] text-sky-200">
                Wastewater treatment & reuse
              </p>
              <h2 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">
                Looking for a site-specific wastewater solution?
              </h2>
              <p className="mt-5 text-base leading-8 text-slate-100/90 sm:text-lg">
                We help design treatment and reuse systems around site
                constraints, water goals, operational realities, and long-term
                reliability.
              </p>
            </div>

            <div className="mt-8 lg:mt-0 lg:pl-8">
              <a
                href="/contact"
                className="inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-semibold text-slate-950 transition duration-300 hover:-translate-y-0.5 hover:bg-sky-50"
              >
                Get in Touch
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}